import React, { useRef, useState } from 'react';
import {
	Box,
	ClickAwayListener,
	InputAdornment,
	MenuItem,
	Paper,
	Popper,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { RiArrowRightLine, RiCalendar2Line } from 'react-icons/ri';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment-timezone';
import useShopFormat from 'hooks/useShopFormat';
import { DataGridDateRange } from './types';
import { useTranslation } from 'services/localization';

export type OptionType =
	| 'default'
	| 'past-week'
	| 'past-month'
	| 'past-3-months'
	| 'custom'
	| 'all';
export interface Props {
	value: DataGridDateRange;
	onChange: (value: DataGridDateRange) => void;
	defaultItem?: {
		limit: number;
		label: string;
	};
	disabled?: boolean;
	visibleOptionsList?: Array<OptionType>;
}

const DEFAULT_LIMIT = 25;

const DateRangePicker = (props: Props) => {
	const { value, onChange, defaultItem, disabled, visibleOptionsList } = props;
	const { t } = useTranslation();

	const { localFormat } = useShopFormat();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const textFieldRef = useRef<HTMLDivElement | null>(null);
	const [customDateRangePickerOpen, setCustomDateRangePickerOpen] = useState<boolean>(false);

	const allOptions: {
		value: OptionType;
		label: string | undefined;
	}[] = [
		{ value: 'default', label: defaultItem?.label },
		{ value: 'past-week', label: t('common:times.pastWeek', 'Past week') },
		{ value: 'past-month', label: t('common:times.pastMonth', 'Past month') },
		{ value: 'past-3-months', label: t('common:times.past3months', 'Past 3 months') },
		{ value: 'custom', label: t('common:times.customDateRange', 'Custom range') },
		{ value: 'all', label: t('common:times.all', 'All') },
	];

	const filteredOptions = !!visibleOptionsList
		? allOptions.filter((option) => visibleOptionsList.includes(option.value))
		: allOptions;

	const handleChangeType = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const type = e.target.value as DataGridDateRange['type'];
		if (type === 'custom') {
			onChange({
				type: 'custom',
				range: [null, null],
			});
		} else {
			if (type === 'default') {
				onChange({ type, limit: defaultItem?.limit ?? DEFAULT_LIMIT });
			} else {
				onChange({ type });
			}
		}
	};

	const handleChangeCustomRange = (value: DateRange<moment.Moment>) => {
		onChange({
			type: 'custom',
			range: value,
		});
	};

	return (
		<Box>
			<TextField
				variant="outlined"
				value={value.type}
				onChange={handleChangeType}
				sx={{ bgcolor: (theme) => theme.palette.background.paper, zIndex: 100 }}
				size="small"
				select
				disabled={disabled}
				ref={textFieldRef}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<RiCalendar2Line size={20} />
						</InputAdornment>
					),
				}}
				SelectProps={{
					renderValue:
						value.type === 'custom'
							? () => (
									<Stack direction="row" alignItems="center" spacing={1}>
										<Typography>
											{value.range[0]
												? localFormat(moment(value.range[0]), 'DD.MM.YYYY')
												: t('common:times.start', 'Start')}
										</Typography>
										<RiArrowRightLine size={16} />
										<Typography>
											{value.range[1]
												? localFormat(moment(value.range[1]), 'DD.MM.YYYY')
												: t('common:times.end', 'End')}
										</Typography>
									</Stack>
							  )
							: undefined,
				}}
			>
				{filteredOptions.map((option, idx) => (
					<MenuItem
						key={idx}
						value={option.value}
						onClick={() => option.value === 'custom' && setCustomDateRangePickerOpen(true)}
					>
						{option.label}
					</MenuItem>
				))}
			</TextField>

			<Popper
				anchorEl={textFieldRef.current}
				open={customDateRangePickerOpen}
				placement="bottom-end"
				popperOptions={{
					strategy: 'fixed',
				}}
				disablePortal
				style={{ zIndex: 20 }}
			>
				<ClickAwayListener onClickAway={() => setCustomDateRangePickerOpen(false)}>
					<Paper
						elevation={4}
						sx={{
							position: 'relative',
							borderRadius: '4px',
							mt: 2,
							zIndex: 1500,
						}}
					>
						<LocalizationProvider
							dateAdapter={AdapterMoment}
							dateLibInstance={moment}
							localeText={{
								start: t('common:times.start', 'Start'),
								end: t('common:times.end', 'End'),
							}}
						>
							<StaticDateRangePicker
								displayStaticWrapperAs={isSmallScreen ? 'mobile' : 'desktop'}
								toolbarTitle={t('common:times.selectDateRange', 'Select date range')}
								value={value.type === 'custom' ? value.range : [null, null]}
								onChange={handleChangeCustomRange}
								disableFuture
								showToolbar
								renderInput={(startProps, endProps) => (
									<Stack direction="row" spacing={1}>
										<TextField
											{...startProps}
											label={t('common:times.from', 'From')}
											InputLabelProps={{ shrink: true }}
										/>
										<TextField
											{...endProps}
											label={t('common:times.to', 'To')}
											InputLabelProps={{ shrink: true }}
										/>
									</Stack>
								)}
							/>
						</LocalizationProvider>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</Box>
	);
};

export default DateRangePicker;
